import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import {
  NanoCheckoutFailureReason,
  trackNanoCheckoutFailed,
  trackNanoCheckoutPaymentSubmitted,
  trackNanoCheckoutPurchased,
} from '../../../lib/analytics/events';
import { ShippingAddress } from '../../../lib/checkout/address';
import { buildApplePayRecurringOption } from '../../../lib/checkout/applePay';
import { CardOnFile } from '../../../lib/checkout/useBillingPrefill';
import { CheckoutOffer } from '../../../lib/checkout/offer';
import { formatCents, PriceBreakdown } from '../../../lib/checkout/pricing';
import { customerMessageFromError, purchaseNanoUpsell } from '../../../lib/checkout/purchase';
import {
  CheckoutCustomerError,
  stripeCheckout,
  ThreeDSecureUnsupportedError,
} from '../../../lib/checkout/stripeCheckout';
import { Links } from '../../../lib/constants';
import styles from './CheckoutStep.module.scss';

export interface PaymentSectionProps {
  email: string;
  address: ShippingAddress;
  /** Stripe's own validation of the address currently in the element */
  addressComplete: boolean;
  /** the card already stored on the shopper's Recurly account, offered instead of the card form */
  cardOnFile: CardOnFile | null;
  offer: CheckoutOffer;
  breakdown: PriceBreakdown;
  petName: string;
  petId: string;
  gwa: boolean;
  onPurchased: () => void;
  /** hand off to the shop for the cases nano cannot finish, notably a 3DS challenge */
  onFallbackToShop: () => void;
}

/**
 * The payment method list and the purchase itself.
 *
 * Must render inside `StripeElementsProvider` -- `useStripe`/`useElements` need the Elements context.
 */
export default function PaymentSection({
  email,
  address,
  addressComplete,
  cardOnFile,
  offer,
  breakdown,
  petName,
  petId,
  gwa,
  onPurchased,
  onFallbackToShop,
}: PaymentSectionProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // which list the shopper is paying from: the saved recurly card, or whatever is selected inside
  // the stripe element (new card, apple pay, google pay, link)
  const [method, setMethod] = useState<'saved' | 'stripe'>(cardOnFile ? 'saved' : 'stripe');
  // the payment method type currently selected inside the stripe element, e.g. 'card' or 'apple_pay'
  const [stripeType, setStripeType] = useState<string | null>(null);
  const lastStripeType = useRef<string | null>(null);

  const payingWithCardOnFile = !!cardOnFile && method === 'saved';

  /* every way to pay renders as one radio list: apple pay and google pay are options inside the
     Payment Element rather than an express button above it (an ExpressCheckoutElement on the page
     would suppress them here), and the card form starts expanded */
  const paymentElementOptions: StripePaymentElementOptions = useMemo(() => {
    // requests an apple pay merchant token (MPAN) so the wallet card can be charged off-session
    // when the membership renews. spread so it is absent entirely rather than `applePay: undefined`
    const applePayRecurring = buildApplePayRecurringOption(offer);
    return {
      layout: { type: 'accordion', radios: true, defaultCollapsed: false },
      // the card form leads; wallets follow rather than headline
      paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'link'],
      ...(applePayRecurring && { applePay: applePayRecurring }),
    };
  }, [offer]);

  const properties = { petId, gwa };

  const fail = (reason: NanoCheckoutFailureReason, message: string) => {
    trackNanoCheckoutFailed(properties, reason);
    setError(message);
  };

  /**
   * The whole purchase, from the selected payment method through to a completed order. With a wallet
   * selected in the element, `createConfirmationToken` opens the wallet sheet for authorization, so
   * one confirm button covers every method.
   */
  const submit = async () => {
    if (!stripe || !elements || submitting) {
      return;
    }

    // fail here rather than sending a bad address to recurly, which rejects the whole billing
    // address ("the billing address on your account is invalid") after the card has already been
    // stored -- so the shopper sees a payment error for what is actually a missing city or zip.
    // `addressComplete` is Stripe's own verdict on the element, which is the only check that catches
    // a malformed field rather than just an empty one.
    if (!addressComplete) {
      trackNanoCheckoutFailed(properties, 'address_error');
      setError('Please complete the shipping address above before confirming.');
      return;
    }

    setSubmitting(true);
    setError(null);
    trackNanoCheckoutPaymentSubmitted(properties);

    try {
      if (payingWithCardOnFile) {
        // the card is already stored as billing info, so this call only saves the shipping address
        await stripeCheckout(null, email, address);
      } else {
        // must run before createConfirmationToken, or stripe throws
        const submitResult = await elements.submit();
        if (submitResult.error) {
          fail('card_declined', submitResult.error.message ?? 'Check your card details and try again.');
          return;
        }

        const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({ elements });
        if (tokenError || !confirmationToken) {
          // closing the wallet sheet is a decision, not a failure
          if (tokenError?.code === 'canceled') {
            return;
          }
          fail('card_declined', tokenError?.message ?? 'We could not read that card. Try another.');
          return;
        }

        // stores the card as billing info and saves the shipping address in one call
        await stripeCheckout(confirmationToken.id, email, address);
      }

      // no token here on purpose: the card is already stored, which is the existing-user path
      await purchaseNanoUpsell(offer, breakdown.shippingCode);

      trackNanoCheckoutPurchased(properties);
      onPurchased();
    } catch (err) {
      if (err instanceof ThreeDSecureUnsupportedError) {
        trackNanoCheckoutFailed(properties, 'three_d_secure');
        onFallbackToShop();
        return;
      }
      if (err instanceof CheckoutCustomerError) {
        fail('card_declined', err.message);
        return;
      }
      // prefer whatever the server said it could tell the shopper: "that coupon has reached the
      // maximum number of redemptions on your account" is actionable, "please try again" is not.
      const serverMessage = customerMessageFromError(err);
      if (serverMessage) {
        fail('card_declined', serverMessage);
        return;
      }
      fail('service_error', 'Something went wrong taking that payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmsApplePay = !payingWithCardOnFile && stripeType === 'apple_pay';
  const confirmsGooglePay = !payingWithCardOnFile && stripeType === 'google_pay';

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.sectionTitle}>Payment</span>
        </div>

        {/* the saved recurly card, drawn as a radio row above the stripe options so the two read as
            one list. stripe cannot render it -- recurly owns the stored card -- so the row is ours. */}
        {cardOnFile && (
          <label className={styles.savedCardRow}>
            <input
              type="radio"
              name="nano-payment-method"
              className={styles.savedCardRadio}
              checked={payingWithCardOnFile}
              onChange={() => setMethod('saved')}
            />
            <span className={styles.addressLine}>
              {cardOnFile.brand} •••• {String(cardOnFile.lastFour).padStart(4, '0')} · Expires {cardOnFile.month}/
              {cardOnFile.year}
            </span>
          </label>
        )}

        {/* stays mounted while the saved card is selected -- apple pay has to be visible to be
            choosable -- but dims so the checked row above reads as the active choice.
            link must stay enabled here: stripe only runs the email lookup and OTP when link can
            actually pay, so suppressing it in this element disables link for the whole group and
            turns the email card above into a plain input */}
        <div className={classNames(payingWithCardOnFile && styles.paymentElementDimmed)}>
          <PaymentElement
            options={paymentElementOptions}
            onChange={(event: StripePaymentElementChangeEvent) => {
              const type = event.value.type;
              // the element fires a change on mount with its default selection; only a switch away
              // from that is the shopper choosing to pay through stripe over the saved card
              if (lastStripeType.current !== null && type !== lastStripeType.current) {
                setMethod('stripe');
              }
              lastStripeType.current = type;
              setStripeType(type);
            }}
            onFocus={() => setMethod('stripe')}
          />
        </div>
      </div>

      {error && <div className={styles.fieldError}>{error}</div>}

      {/* One sentence next to the charge: what today buys, what happens after, and how to get out.
          The credit is deliberately not restated -- `OrderSummary` directly above already renders it
          as its own signed line, so the arithmetic is visible rather than claimed. Today's figure
          names the hardware as well as the months, because pricing only the membership let a reader
          assume the tracker was extra. The renewal figure comes from the offer, since it is a claim
          about what web will actually schedule.
          The renewal figure carries the only bold. Today's total is already the heaviest thing on the
          screen in OrderSummary's total row, so emphasising it again here says nothing; the renewal is
          the one fact in this sentence that appears nowhere else, and it is the part a skimmer must
          not miss. One bold, not two -- two in a 13px grey sentence reads striped and cancels out. */}
      <p className={styles.disclosure}>
        {formatCents(breakdown.totalInCents)} today covers the Fi Mini tracker and {petName}’s first 6 months
        {offer.renewalText ? (
          <>
            , then <strong>{offer.renewalText}</strong>
          </>
        ) : (
          ''
        )}{' '}
        — we’ll email you before it renews, and you can cancel any time from the app.
      </p>

      <div className={styles.ctaContainer}>
        {confirmsApplePay ? (
          /* safari draws the apple pay mark itself via -webkit-appearance, per apple's mark rules;
             the button carries no text of its own */
          <button
            className={styles.ctaApplePay}
            type="button"
            disabled={submitting}
            aria-label="Pay with Apple Pay"
            onClick={() => void submit()}
          />
        ) : confirmsGooglePay ? (
          <button className={styles.ctaGooglePay} type="button" disabled={submitting} onClick={() => void submit()}>
            {submitting ? 'Confirming…' : 'Pay with Google Pay'}
          </button>
        ) : (
          <button className={styles.ctaPrimary} type="button" disabled={submitting} onClick={() => void submit()}>
            {submitting ? 'Confirming…' : 'Confirm'}
          </button>
        )}
        {/* the exit lives with the action rather than above it, so declining is always one tap away */}
        <a className={styles.ctaGhost} href={Links.manageHome}>
          Not now
        </a>
      </div>
    </>
  );
}
